import type { StatCell, VerificationFlagId } from "@sins2-atlas/game-data";

// The honesty language as shown in the UI (legend + cell tooltips) — the concise form of
// CONTEXT.md's definitions, worded by the user (2026-07-28); the glossary keeps the full
// versions.
export const STATE_META = {
  computed: {
    label: "Computed",
    description: "Obtained from game data",
  },
  not_applicable: {
    glyph: "—",
    label: "Not Applicable",
    description: "The stat does not exist for this unit",
  },
  formula_unverified: {
    glyph: "≈",
    label: "Formula Unverified",
    description: "Inputs exist, but the formula is not confirmed",
  },
  not_in_game_data: {
    glyph: "∅",
    label: "Not In Game Data",
    description: "Unable to extract from game data",
  },
} as const;

export const FLAG_LABELS: Record<VerificationFlagId, string> = {
  burst_multiplicity: "whether a burst weapon's damage is per shot or per volley",
  torpedo_delivery: "whether torpedo tooltip DPS equals damage ÷ cooldown",
  research_stacking: "how researched stat bonuses stack into one value",
  damage_formula: "an in-game damage reading confirming the mitigation formula",
  corruption_buff_inheritance:
    "whether a unit that declares no Corruption buffs of its own receives its player's",
};

/** Sorting: Computed cells sort by value; every other state returns undefined so TanStack's
 * sortUndefined:"last" pins them to the end in BOTH directions, in stable row order. */
export function cellSortValue(cell: StatCell): number | undefined {
  return cell.state === "computed" ? cell.value : undefined;
}

const numberFormat = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 4 });

/** Format a computed value: grouped, at most 4 significant digits (301.333333 → "301.3",
 * 0.013333 → "0.01333", 12000 → "12,000"). */
export function formatStatNumber(value: number): string {
  return numberFormat.format(value);
}

/** The hover/tooltip text for a cell — state honesty language plus the cell's own reason,
 * gates, or captions. Empty for a caption-less Computed value. */
export function cellTitle(cell: StatCell): string {
  switch (cell.state) {
    case "computed":
      return (cell.captions ?? []).join("; ");
    case "not_applicable":
      return `${STATE_META.not_applicable.label}: ${cell.reason}. ${STATE_META.not_applicable.description}`;
    case "formula_unverified":
      return `${STATE_META.formula_unverified.label}. Waiting on: ${cell.flags
        .map((f) => FLAG_LABELS[f])
        .join("; ")}. ${STATE_META.formula_unverified.description}`;
    case "not_in_game_data":
      return `${STATE_META.not_in_game_data.label}: ${cell.reason}. ${STATE_META.not_in_game_data.description}`;
  }
}
